import { Badge } from '@/components/ui/badge';
import { OnboardingChrome } from '@/components/OnboardingChrome';
import { OnboardingResultsPending } from '@/components/OnboardingResultsPending';
import { OnboardingResultsAreIn } from '@/pages/OnboardingResultsAreIn';
import { IncompleteIntakeAlert } from '@/shared/components/incomplete-intake-alert';

import { ClipboardList, SlidersHorizontal, PiggyBank } from 'lucide-react';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { motion } from 'framer-motion';
import { SessionType } from '@shared';
import { resetOnboardingSlideTracker, selectClient, selectSession } from '@/model';

interface Step {
  number: number;
  icon: React.ReactNode;
  title: string;
  description: string;
  isOptional?: boolean;
}

const STEPS: Step[] = [
  {
    number: 1,
    icon: <ClipboardList className="size-6 text-gray-700" />,
    title: 'Review Your Results',
    description:
      'Understand your predicted care needs, personalized using over 500 million datapoints from people like you.',
  },
  {
    number: 2,
    icon: <SlidersHorizontal className="size-6 text-gray-700" />,
    title: 'Personalize Your Plan',
    description:
      'Refine your plan with your place of care and anticipated family caregiving support at each phase.',
    isOptional: true,
  },
  {
    number: 3,
    icon: <PiggyBank className="size-6 text-gray-700" />,
    title: 'Evaluate Your Funding Solutions',
    description: 'Explore multiple recommended ways to protect you and fund your LTC needs.',
  },
];

export function OnboardingOverview(_props: ClientIdProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const session = useSelector(selectSession);
  const { clientFirstName } = client;

  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const [visibleLines, setVisibleLines] = useState<number[]>([]);

  const isClientSession = session.sessionType === SessionType.client;
  const clientHasNoAccess = isClientSession && client.hasAccessToResults !== true;

  useEffect(() => {
    dispatch(resetOnboardingSlideTracker());
  }, [dispatch]);

  if (clientHasNoAccess) {
    return <OnboardingResultsPending />;
  }

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    const baseDelay = 150;
    const cardDuration = 350;
    const lineDuration = 350;

    STEPS.forEach((step, index) => {
      const cardDelay = baseDelay + index * (cardDuration + lineDuration);
      const cardTimer = setTimeout(() => {
        setVisibleSteps(prev => [...prev, step.number]);
      }, cardDelay);

      timers.push(cardTimer);

      if (index < STEPS.length - 1) {
        const lineDelay = cardDelay + cardDuration;
        const lineTimer = setTimeout(() => {
          setVisibleLines(prev => [...prev, step.number]);
        }, lineDelay);

        timers.push(lineTimer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <OnboardingChrome
      slidePage={OnboardingOverview}
      nextButtonLabel="Let's Get Started"
      nextSlidePage={OnboardingResultsAreIn}
      hideProgressStepper
    >
      <div className="flex flex-col items-center justify-center py-8 md:py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-2 text-center"
        >
          <h1 className="text-balance text-3xl font-bold text-gray-900 md:text-4xl">
            Building {clientFirstName}&apos;s{' '}
            <span className="text-primary">Long-Term Care Plan</span>
          </h1>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-balance text-center text-base text-gray-700 md:text-lg"
        >
          Understand your needs, personalize your care, and choose the best funding options
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-6 w-full max-w-2xl"
        >
          <IncompleteIntakeAlert />
        </motion.div>
        <div className="relative mt-12 w-full max-w-2xl md:mt-16">
          {STEPS.map((step, index) => {
            const isLastStep = index === STEPS.length - 1;
            const isStepVisible = visibleSteps.includes(step.number);

            return (
              <div
                key={step.number}
                className="relative flex"
              >
                <div className="relative mr-4 flex flex-col items-center md:mr-6">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isStepVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                    transition={{
                      duration: 0.2,
                      type: 'spring',
                      stiffness: 250,
                      damping: 15,
                    }}
                    className="relative"
                  >
                    <div className="flex size-12 items-center justify-center rounded-xl border-2 border-gray-200 bg-white md:size-14">
                      {step.icon}
                    </div>
                    <div className="bg-primary text-primary-foreground border-background absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full border-2 text-xs font-semibold">
                      {step.number}
                    </div>
                  </motion.div>

                  {!isLastStep && (
                    <motion.div
                      initial={{ scaleY: 0 }}
                      animate={visibleLines.includes(step.number) ? { scaleY: 1 } : { scaleY: 0 }}
                      transition={{ duration: 0.1, ease: 'easeOut' }}
                      className="min-h-20 w-0.5 flex-1 origin-top bg-gray-200 md:min-h-16"
                    />
                  )}
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={isStepVisible ? { opacity: 1, x: 0 } : { opacity: 0 }}
                  transition={{ duration: 0.3, delay: 0.15 }}
                  className="flex-1"
                >
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-950 md:text-lg">
                      {step.title}
                    </h3>
                    {step.isOptional && <Badge variant="secondary">Optional</Badge>}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-gray-700 md:text-base">
                    {step.description}
                  </p>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </OnboardingChrome>
  );
}
